import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { analytics } from '../services/analytics'

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, _from, _savedPosition) {
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' } // Smooth scrolling with bounce
    }
    return { top: 0, behavior: 'smooth' } // Smooth scrolling with bounce
  },
  routes: [
    {
      path: '/',
      name: 'landing',
      component: () => import('../views/LandingView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/features',
      name: 'features',
      component: () => import('../views/LandingView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/maintenance-mode',
      name: 'maintenance-mode',
      component: () => import('../views/MaintenanceView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/pricing',
      name: 'pricing',
      component: () => import('../views/PricingView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/subscription-preview',
      name: 'subscription-preview',
      component: () => import('../views/SubscriptionDemo.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/chat',
      name: 'chat',
      component: () => import('../views/ChatView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/careers',
      name: 'careers',
      component: () => import('../views/CareersView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/TermsView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/line-of-business',
      name: 'line-of-business',
      component: () => import('../views/LineOfBusinessView.vue'),
      meta: { requiresAuth: false }
    },
    { path: '/about/medical-accuracy', name: 'medical-accuracy', component: () => import('../views/MedicalAccuracy.vue'), meta: { requiresAuth: false } },
    { path: '/about/privacy-first', name: 'privacy-first', component: () => import('../views/PrivacyFirst.vue'), meta: { requiresAuth: false } },
    { path: '/about/accessible', name: 'accessible', component: () => import('../views/AccessibleView.vue'), meta: { requiresAuth: false } },
    { path: '/pharmacodynamics', name: 'pharmacodynamics', component: () => import('../views/Pharmacodynamics.vue'), meta: { requiresAuth: false } },
    {
      path: '/auth',
      name: 'auth',
      component: () => import('../views/AuthView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/auth/google/callback',
      name: 'google-oauth-callback',
      component: () => import('../views/GoogleOAuthCallback.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/dashboard',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
      meta: { requiresAuth: true },
      props: route => ({ tab: route.query.tab })
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/ProfileView.vue'),
      meta: { requiresAuth: true },
      props: route => ({ tab: route.query.tab })
    },
    {
      path: '/blood-concentration',
      name: 'blood-concentration',
      component: () => import('../views/BloodConcentration.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/drug-tolerance',
      name: 'drug-tolerance',
      component: () => import('../views/DrugTolerance.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/smart-journal',
      name: 'smart-journal',
      component: () => import('../views/SmartMedicationJournal.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/smart-medication-journal',
      name: 'smart-medication-journal',
      component: () => import('../views/SmartMedicationJournal.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/mental-health-companion',
      name: 'mental-health-companion',
      component: () => import('../views/MentalHealthCompanion.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/ai-health-assistant',
      name: 'ai-health-assistant',
      component: () => import('../views/AIHealthAssistant.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/personalized-precision',
      name: 'personalized-precision',
      component: () => import('../views/PersonalizedPrecision.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/wellness-goal-tracking',
      name: 'wellness-goal-tracking',
      component: () => import('../views/WellnessGoalTracking.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/drug-search',
      name: 'drug-search',
      component: () => import('../views/DrugSearchView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('../views/AdminView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../components/ResetPasswordForm.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/feedback',
      name: 'feedback',
      component: () => import('../views/FeedbackView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/conditions',
      name: 'conditions',
      component: () => import('../views/ConditionsView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('../views/EmailVerificationView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/drug-interactions',
      name: 'drug-interactions',
      component: () => import('../views/DrugInteractionsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/whats-new',
      name: 'whats-new',
      component: () => import('../views/WhatsNewView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/talk-with-edvinas',
      name: 'talk-with-edvinas',
      component: () => import('../views/EdvinasChatView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/mobile-app',
      name: 'mobile-app',
      component: () => import('../views/MobileAppView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/demo',
      name: 'demo',
      component: () => import('../views/DemoView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/modules/:id',
      name: 'module-details',
      component: () => import('../views/ModuleDetailsView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/technology/:component',
      name: 'technology-component',
      component: () => import('../views/TechnologyComponentView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/about/engineering-led',
      name: 'engineering-led',
      component: () => import('../views/EngineeringLedView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/about/transparent-logic',
      name: 'transparent-logic',
      component: () => import('../views/TransparentLogicView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/about/audit-ready',
      name: 'audit-ready',
      component: () => import('../views/AuditReadyView.vue'),
      meta: { requiresAuth: false }
    },
    // Catch-all route for 404 errors
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      meta: { requiresAuth: false }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  try {
    const authStore = useAuthStore()
    const { useFeaturesStore } = await import('../stores/features')
    const featuresStore = useFeaturesStore()
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
    const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)

    // Check pricing feature flag - redirect if disabled
    if (to.path === '/pricing' && !featuresStore.isPricingEnabled) {
      next('/')
      return
    }

    // Check try demo feature flag - redirect if disabled
    if (to.path === '/demo' && !featuresStore.isTryDemoEnabled) {
      next('/')
      return
    }

    // Check maintenance mode (allow admins and auth page to bypass)
    const isAuthPage = to.path === '/auth' || to.path.startsWith('/auth/')
    const isMaintenancePage = to.path === '/maintenance-mode'
    
    // Always allow access to auth page and maintenance page
    if (!isAuthPage && !isMaintenancePage) {
      try {
        const apiService = (await import('../services/apiService')).default
        const m = await apiService.getMaintenance()
        if (m?.enabled && (m as any).full_lock) {
          // Allow admins to bypass maintenance mode
          const isAdmin = authStore.isAuthenticated && authStore.user?.is_admin
          if (!isAdmin) {
            const q = m.message ? `?msg=${encodeURIComponent(m.message)}` : ''
            next({ path: `/maintenance-mode${q}`, replace: true })
            return
          }
        }
      } catch (error) {
        // Silently ignore maintenance check errors
        console.error('Error checking maintenance mode:', error)
      }
    }

    // Ensure user info is loaded before making role-based decisions on hard refresh
    if ((requiresAuth || requiresAdmin) && authStore.isAuthenticated && !authStore.user) {
      try {
        await authStore.fetchUserData()
      } catch (error) {
        console.error('Failed to fetch user data during navigation:', error)
        // If user data fetch fails, redirect to auth page
        if (requiresAuth) {
          next({ path: '/auth', query: from.query })
          return
        }
      }
    }

    if (requiresAuth && !authStore.isAuthenticated) {
      next({ path: '/auth', query: from.query })
    } else if (requiresAdmin && (!authStore.user || !authStore.user.is_admin)) {
      // Redirect to dashboard if user is not admin
      next('/dashboard')
    } else if (to.path === '/auth' && authStore.isAuthenticated) {
      next('/dashboard')
    } else if (to.path === '/' && authStore.isAuthenticated) {
      // If signed in and visiting landing page, go to home
      next('/dashboard')
    } else {
      next()
    }
  } catch (error) {
    console.error('Router navigation error:', error)
    // Fallback to landing page on any navigation error
    next('/')
  }
})

router.afterEach((to) => {
  // Track page views
  analytics.trackPageView(to.path, to.name as string)
})

export default router 
